import { html, nothing } from "lit";
import { icons } from "../icons.ts";

/**
 * BIBLE §3 — *Blokady, błędy i pytania wymagające decyzji użytkownika są
 * widoczne bez otwierania aktywności agentów.*
 *
 * The ux-audit found the chat surface had no dedicated place for
 * "blocked / needs decision / failed". Those states only existed deep
 * inside the Inspector panel, so the operator had to open a second surface
 * to learn that work was stuck.
 *
 * This strip is the always-visible surface for those states. It sits
 * directly above the composer, so it is in the operator's natural reading
 * position and never requires opening a panel.
 */

export type ChatStatusStripItem = {
  id: string;
  /** `blocked` needs a decision, `error` failed, `warning` needs attention. */
  tone: "blocked" | "error" | "warning" | "info";
  label: string;
  detail?: string | null;
  /** Optional action, e.g. "Open Inspector" or "Resolve". */
  actionLabel?: string | null;
  onAction?: () => void;
};

export type ChatStatusStripProps = {
  items: ChatStatusStripItem[];
  onDismiss?: (id: string) => void;
};

const TONE_ICON: Record<ChatStatusStripItem["tone"], unknown> = {
  blocked: icons.alertTriangle,
  error: icons.alertTriangle,
  warning: icons.alertTriangle,
  info: icons.info,
};

const TONE_LABEL: Record<ChatStatusStripItem["tone"], string> = {
  blocked: "Needs decision",
  error: "Error",
  warning: "Attention",
  info: "Status",
};

export function renderChatStatusStrip(props: ChatStatusStripProps) {
  const items = props.items.filter((item) => item.label.trim().length > 0);
  if (items.length === 0) {
    return nothing;
  }
  return html`
    <div
      class="chat-status-strip"
      role="status"
      aria-live="polite"
      aria-label="Chat status"
      data-testid="chat-status-strip"
    >
      ${items.map(
        (item) => html`
          <div
            class="chat-status-strip__item chat-status-strip__item--${item.tone}"
            data-testid="chat-status-strip-item"
          >
            <span class="chat-status-strip__icon" aria-hidden="true">${TONE_ICON[item.tone]}</span>
            <span class="chat-status-strip__body">
              <span class="chat-status-strip__label">
                <span class="chat-status-strip__tone">${TONE_LABEL[item.tone]}</span>
                <span class="chat-status-strip__text">${item.label}</span>
              </span>
              ${item.detail?.trim()
                ? html`<span class="chat-status-strip__detail">${item.detail}</span>`
                : nothing}
            </span>
            ${item.actionLabel && item.onAction
              ? html`
                  <button
                    type="button"
                    class="chat-status-strip__action"
                    @click=${() => item.onAction?.()}
                  >
                    ${item.actionLabel}
                  </button>
                `
              : nothing}
            ${props.onDismiss
              ? html`
                  <button
                    type="button"
                    class="chat-status-strip__dismiss"
                    aria-label="Dismiss ${item.label}"
                    title="Dismiss"
                    @click=${() => props.onDismiss?.(item.id)}
                  >
                    ${icons.x}
                  </button>
                `
              : nothing}
          </div>
        `,
      )}
    </div>
  `;
}
